/**
 * CubeEffect creates a 3D cube rotation transition between slides.
 *
 * Inspired by Instagram Stories transitions, slides rotate around their edges
 * to create the appearance of faces of a cube rotating into view.
 *
 * The effect uses two main transition ranges:
 * - L1 → CL1: Incoming slide rotates around its RIGHT edge (90° → 0°)
 * - CL1 → R1: Outgoing slide rotates around its LEFT edge (0° → -90°)
 *
 * This creates seamless cube face transitions where the shared edge between
 * adjacent slides acts as the pivot point for the 3D rotation.
 */

// Factory function - class is created when TarotEffect is available
function createCubeEffect(TarotEffect) {
	return class CubeEffect extends TarotEffect {
		static effectName = 'cube';

		/**
		 * Effect configuration rules that define carousel behavior constraints
		 */
		static rules = {
			minSlidesPerView: 1,
			maxSlidesPerView: 1, // Cube effect works best with one slide at a time
			loopBuffer: { left: 1, right: 1 }, // Need adjacent slides for cube faces
		};

		/**
		 * Creates a new cube effect instance
		 * @param {Object} ctx - The shared context object
		 */
		constructor(ctx) {
			super(ctx);
			this.init();
		}

		init() {
			super.init();
		}

		reInit() {
			super.reInit();
		}

		/**
		 * Main render function called every animation frame to create cube rotations
		 * Uses the frame-based architecture with dependency injection for utilities
		 *
		 * @param {Object} frame - Complete frame object with all carousel data
		 * @param {Array} frame.slides - Sorted slides array with calculated positions
		 * @param {Object} frame.widths - Layout measurements
		 * @param {Object} frame.state - Carousel state
		 * @param {Object} frame.animation - Animation data including trackPosition
		 * @param {Object} frame.transformPoints - Named position points
		 * @param {Object} utils - Frame utilities for position calculations
		 * @param {Function} utils.isSlideInRange - Check if slide is in range with progress
		 */
		render(frame, utils) {
			const { slides, widths, animation } = frame;

			// Cube effect uses calculated slide width, track position is virtual only
			this.renderSlideWidth(widths.slide);
			// Note: renderTrackPosition() not called - track DOM position handled per-slide

			// Process each slide to determine its cube rotation state
			for (let slideIndex = 0, totalSlides = slides.length; slideIndex < totalSlides; slideIndex++) {
				const slide = slides[slideIndex];

				// Frame engine has already set trackPosition and centerPoint properties

				// Check if slide is in the center visible position
				const centerRange = utils.isSlideInRange(slide, 'CR1', 'CL1');
				if (centerRange.isInRange) {
					this.applyCenterCubeFace(slide, animation.isAnimating, widths);
					continue;
				}

				// Check if slide is in the incoming transition range (L1 → CL1)
				const incomingRange = utils.isSlideInRange(slide, 'CL1', 'L1');
				if (incomingRange.isInRange) {
					this.applyIncomingCubeFace(
						slide,
						incomingRange.percent,
						animation.isAnimating,
						widths
					);
					continue;
				}

				// Check if slide is in the outgoing transition range (CR1 → R1)
				const outgoingRange = utils.isSlideInRange(slide, 'R1', 'CR1');
				if (outgoingRange.isInRange) {
					this.applyOutgoingCubeFace(
						slide,
						outgoingRange.percent,
						animation.isAnimating,
						widths
					);
					continue;
				}

				// Outside visible cube ranges - hide the slide
				this.applyHiddenState(slide);
			}
		}

		/**
		 * Apply styling for center cube face (CL1 → CR1)
		 * The visible slide in the center with no rotation
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {boolean} isAnimating - Whether carousel is currently animating
		 * @param {Object} widths - Width measurements including paddingLeft
		 */
		applyCenterCubeFace(slide, isAnimating, widths) {
			// Center slide positioned at paddingLeft offset to respect viewport margins
			const translateX = widths.paddingLeft;

			// Use translate3d during animation for GPU acceleration, translateX when static
			const transformValue = isAnimating
				? `translate3d(${translateX}px, 0, 0)`
				: `translateX(${translateX}px)`;

			slide.style.transform = transformValue;
			slide.style.transformOrigin = 'center center';
			slide.style.transformStyle = 'preserve-3d'; // Safari 3D compatibility
			slide.style.zIndex = '3'; // Center slide on top
			slide.style.opacity = '1';
			slide.show();
		}

		/**
		 * Apply cube rotation for incoming slides (L1 → CL1)
		 * Slides rotate around their RIGHT edge from 90° (invisible) to 0° (visible)
		 * Fixed positioning like stack/peacock effects - not based on trackPosition
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} transitionPercent - Progress through transition (0 = at L1, 1 = at CL1)
		 * @param {boolean} isAnimating - Whether carousel is currently animating
		 * @param {Object} widths - Width measurements including paddingLeft and slide width
		 */
		applyIncomingCubeFace(slide, transitionPercent, isAnimating, widths) {
			// Calculate rotation: -90° at L1 (invisible edge-on) → 0° at CL1 (visible face-on)
			// Negative rotation makes it feel like we're outside the cube looking at it
			const maxRotation = 90; // degrees
			const rotateAmount = -maxRotation * (1 - transitionPercent); // -90° → 0°

			// Fixed positioning for cube effect - incoming slide transitions to paddingLeft position
			// When rotateY(-90°), slide is edge-on and positioned at left of center slide
			// When rotateY(0°), slide is face-on and positioned at paddingLeft (center position)
			const slideWidth = widths.slide;
			const basePosition = widths.paddingLeft;
			const translateX = basePosition + (-slideWidth * (1 - transitionPercent)); // paddingLeft-slideWidth → paddingLeft

			// Use translate3d during animation for GPU acceleration, translateX when static
			const transformValue = isAnimating
				? `translate3d(${translateX}px, 0, 0) rotateY(${rotateAmount}deg)`
				: `translateX(${translateX}px) rotateY(${rotateAmount}deg)`;

			slide.style.transform = transformValue;
			slide.style.transformOrigin = 'right center'; // Rotate around right edge
			slide.style.transformStyle = 'preserve-3d'; // Safari 3D compatibility
			slide.style.zIndex = '2'; // Incoming slide above outgoing
			slide.style.opacity = '1';
			slide.show();
		}

		/**
		 * Apply cube rotation for outgoing slides (CR1 → R1)
		 * Slides rotate around their LEFT edge from 0° (visible) to -90° (invisible)
		 * Fixed positioning like stack/peacock effects - not based on trackPosition
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} transitionPercent - Progress through transition (0 = at CR1, 1 = at R1)
		 * @param {boolean} isAnimating - Whether carousel is currently animating
		 * @param {Object} widths - Width measurements including paddingLeft and slide width
		 */
		applyOutgoingCubeFace(slide, transitionPercent, isAnimating, widths) {
			// Calculate rotation: 0° at CR1 (visible face-on) → 90° at R1 (invisible edge-on)
			// Positive rotation makes it feel like we're outside the cube looking at it
			const maxRotation = 90; // degrees
			const rotateAmount = maxRotation * transitionPercent; // 0° → 90°

			// Fixed positioning for cube effect - outgoing slide starts at paddingLeft and transitions out
			// When rotateY(0°), slide is face-on and positioned at paddingLeft (center position)
			// When rotateY(90°), slide is edge-on and positioned at right edge of center position
			const slideWidth = widths.slide;
			const basePosition = widths.paddingLeft;
			const translateX = basePosition + (slideWidth * transitionPercent); // paddingLeft → paddingLeft+slideWidth

			// Use translate3d during animation for GPU acceleration, translateX when static
			const transformValue = isAnimating
				? `translate3d(${translateX}px, 0, 0) rotateY(${rotateAmount}deg)`
				: `translateX(${translateX}px) rotateY(${rotateAmount}deg)`;

			slide.style.transform = transformValue;
			slide.style.transformOrigin = 'left center'; // Rotate around left edge
			slide.style.transformStyle = 'preserve-3d'; // Safari 3D compatibility
			slide.style.zIndex = '1'; // Outgoing slide below incoming
			slide.style.opacity = '1';
			slide.show();
		}

		/**
		 * Hide slides that are outside the visible cube rotation ranges
		 * This applies to slides beyond the L1-CL1-R1 transition area
		 *
		 * @param {HTMLElement} slide - The slide element to hide
		 */
		applyHiddenState(slide) {
			slide.style.opacity = '0';
			slide.hide();
			slide.style.zIndex = '0';
			slide.style.transform = 'translate3d(0, 0, 0)'; // Reset transform
			slide.style.transformOrigin = 'center center';
			slide.style.transformStyle = ''; // Reset Safari fix
		}

		/**
		 * Clean up the effect when it's being destroyed or replaced
		 * Resets all slide transforms and styles to default state
		 */
		destroy() {
			super.destroy();

			// Reset all slides to default state
			const slides = this.ctx.store.getSlides();
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];
				slide.style.transform = '';
				slide.style.transformOrigin = '';
				slide.style.transformStyle = ''; // Reset Safari fix
				slide.style.opacity = '';
				slide.style.zIndex = '';
				slide.show?.();
			}
		}
	};
}

// Queue the factory (works regardless of whether Tarot is loaded)
(window.TarotEffectQueue = window.TarotEffectQueue || []).push({
	effectName: 'cube',
	factory: createCubeEffect
});

export default createCubeEffect;
